import type { IProductInformationFormData } from "@/context/data.types";
import { YES_NO_OPTIONS } from "@/constants/yesNo";
import {
  type ProductSpecificationTypeListItem,
  isProduceRacBrandedSpecificationType,
  isProduceRacTjLabelSpecificationType,
} from "@/utils/productSpecificationTypeUtils";

export const OPP_BUY_INDICATOR_ORGANIC = "Organic";
export const OPP_BUY_INDICATOR_NON_ORGANIC = "Non-Organic";

export type OpportunityBuyModalText = {
  ORGANIC?: string;
  NON_ORGANIC?: string;
};

export type OpportunityBuyButtonText = {
  YES?: string;
  NO?: string;
};

export type CreateProductModalValues = {
  productSpecificationType: string;
  opportunityBuy: string;
  oppBuyIndicator: string;
};

export const getOpportunityBuyOptions = (buttonTexts?: OpportunityBuyButtonText) =>
  YES_NO_OPTIONS.map((option, index) => ({
    label: index === 0 ? (buttonTexts?.YES ?? option.label) : (buttonTexts?.NO ?? option.label),
    value: option.value,
  }));

export const getOppBuyIndicatorOptions = (modalText?: OpportunityBuyModalText) => [
  { label: modalText?.ORGANIC ?? OPP_BUY_INDICATOR_ORGANIC, value: OPP_BUY_INDICATOR_ORGANIC },
  {
    label: modalText?.NON_ORGANIC ?? OPP_BUY_INDICATOR_NON_ORGANIC,
    value: OPP_BUY_INDICATOR_NON_ORGANIC,
  },
];

export const OPP_BUY_ORGANIC_OR_NON_ORGANIC_API_KEY = "oppBuyOrganicOrNonOrganicIndicator";

export const normalizeOppBuyIndicatorValue = (value: unknown): string => {
  if (value == null || value === "") return "";

  const raw =
    typeof value === "object"
      ? String(
          (value as { id?: string; value?: string; label?: string; attributeValue?: string }).id ??
            (value as { value?: string }).value ??
            (value as { attributeValue?: string }).attributeValue ??
            (value as { label?: string }).label ??
            ""
        )
      : String(value);

  const normalized = raw.toLowerCase().replace(/[\s_-]+/g, "");
  if (!normalized) return "";

  if (normalized.includes("non") && normalized.includes("organic")) {
    return OPP_BUY_INDICATOR_NON_ORGANIC;
  }
  if (normalized.includes("organic")) {
    return OPP_BUY_INDICATOR_ORGANIC;
  }

  return raw.trim();
};

export const getOppBuyIndicatorFromIdeaResponse = (resp?: Record<string, unknown> | null): string =>
  normalizeOppBuyIndicatorValue(
    resp?.[OPP_BUY_ORGANIC_OR_NON_ORGANIC_API_KEY] ?? resp?.oppBuyIndicator
  );

export const mapProductInformationFormToIdeaPayload = (
  form: IProductInformationFormData | Record<string, unknown>
): Record<string, unknown> => {
  const { oppBuyIndicator, ...rest } = form as Record<string, unknown>;
  const payload = { ...rest };
  const indicator = normalizeOppBuyIndicatorValue(oppBuyIndicator);

  if (indicator) {
    payload[OPP_BUY_ORGANIC_OR_NON_ORGANIC_API_KEY] = indicator;
  }

  return payload;
};

export const requiresOpportunityBuy = (
  productSpecificationType: string,
  specificationTypeList?: ProductSpecificationTypeListItem[]
): boolean =>
  isProduceRacBrandedSpecificationType(productSpecificationType, specificationTypeList);

export const isProduceRacBrandedOpportunityBuyYes = (
  storedValue?: string | null,
  opportunityBuy?: string | null,
  specificationTypeList?: ProductSpecificationTypeListItem[]
): boolean =>
  isProduceRacBrandedSpecificationType(storedValue, specificationTypeList) &&
  String(opportunityBuy ?? "").trim() === (YES_NO_OPTIONS[0]?.value ?? "Yes");

/** Retail Price, Product Size, and Case Pack — mandatory on Product Information for applicable RAC layouts. */
export const PRODUCE_RAC_PRODUCT_INFORMATION_PRICING_FIELDS = [
  "retailPrice",
  "size",
  "packSize",
] as const satisfies readonly (keyof IProductInformationFormData)[];

/**
 * True when Product Information must require Retail Price, Product Size, and Case Pack:
 * - Produce RAC - Trader Joe's Label, or
 * - Produce RAC - Branded with Opportunity Buy ≠ Yes.
 */
export const requiresProduceRacProductInformationPricingFields = (
  productSpecificationType?: string | null,
  opportunityBuy?: string | null,
  specificationTypeList?: ProductSpecificationTypeListItem[]
): boolean => {
  if (
    isProduceRacTjLabelSpecificationType(productSpecificationType, specificationTypeList)
  ) {
    return true;
  }

  return (
    isProduceRacBrandedSpecificationType(productSpecificationType, specificationTypeList) &&
    !isProduceRacBrandedOpportunityBuyYes(
      productSpecificationType,
      opportunityBuy,
      specificationTypeList
    )
  );
};

export const getMissingProduceRacProductInformationPricingFields = (
  form: Pick<IProductInformationFormData, "retailPrice" | "size" | "packSize">,
  productSpecificationType?: string | null,
  opportunityBuy?: string | null,
  specificationTypeList?: ProductSpecificationTypeListItem[]
): (keyof IProductInformationFormData)[] => {
  if (
    !requiresProduceRacProductInformationPricingFields(
      productSpecificationType,
      opportunityBuy,
      specificationTypeList
    )
  ) {
    return [];
  }

  return PRODUCE_RAC_PRODUCT_INFORMATION_PRICING_FIELDS.filter(
    field => !form[field]
  );
};

export const PRODUCE_RAC_BRANDED_OPP_BUY_YES_HIDDEN_FIELDS = [
  "productType",
  "requiredAttributes",
  "sellingPeriod",
  "reasonForDevelopment",
  "estWeeklyUnitVol",
  "potentialSupplier",
  "launchDate",
  "retailPrice",
  "size",
  "packSize",
] as const satisfies readonly (keyof IProductInformationFormData)[];

export const isProduceRacBrandedOppBuyYesHiddenField = (
  field: keyof IProductInformationFormData,
  storedValue?: string | null,
  opportunityBuy?: string | null,
  specificationTypeList?: ProductSpecificationTypeListItem[]
): boolean =>
  isProduceRacBrandedOpportunityBuyYes(storedValue, opportunityBuy, specificationTypeList) &&
  (PRODUCE_RAC_BRANDED_OPP_BUY_YES_HIDDEN_FIELDS as readonly string[]).includes(field);

export const requiresOppBuyIndicator = (
  productSpecificationType: string,
  opportunityBuy: string,
  specificationTypeList?: ProductSpecificationTypeListItem[]
): boolean =>
  isProduceRacBrandedOpportunityBuyYes(
    productSpecificationType,
    opportunityBuy,
    specificationTypeList
  );

export const mergeSpecificationTypeIntoForm = (
  prev: IProductInformationFormData,
  { productSpecificationType, opportunityBuy, oppBuyIndicator }: CreateProductModalValues,
  specificationTypeList?: ProductSpecificationTypeListItem[]
): IProductInformationFormData => ({
  ...prev,
  productSpecificationType,
  opportunityBuy: requiresOpportunityBuy(productSpecificationType, specificationTypeList)
    ? opportunityBuy
    : "",
  oppBuyIndicator: requiresOppBuyIndicator(
    productSpecificationType,
    opportunityBuy,
    specificationTypeList
  )
    ? oppBuyIndicator
    : "",
});
